import type {
	BlockCardDefinition,
	EmbedCardDefinition,
	ParagraphDefinition,
} from '@atlaskit/adf-schema';

import type { SmartLinkDisplayType } from '../../layout/types';
import type { AdfContentNode, TagConverter } from '../types';

/**
 * Default layout for embed cards
 */
const DEFAULT_EMBED_LAYOUT = 'wide' as const;

/**
 * Converts a Lumina smart-link tag to an ADF card node.
 *
 * Supports three display types:
 * - 'inline' (default): Renders as an inlineCard wrapped in a paragraph
 * - 'block': Renders as a blockCard - full-width card with preview
 * - 'embed': Renders as an embedCard - embedded content with dimensions
 *
 * Returns undefined if no URL is provided.
 */
export const convertSmartLink: TagConverter = (luminaNode): AdfContentNode | undefined => {
	const attrs = luminaNode.attributes as Record<string, string | undefined> | undefined;
	const url = attrs?.url;

	// Smart links require a URL to be valid
	if (!url) {
		return undefined;
	}

	const display = (attrs?.display ?? 'inline') as SmartLinkDisplayType;

	switch (display) {
		case 'block': {
			const blockCard: BlockCardDefinition = {
				type: 'blockCard',
				attrs: {
					url,
				},
			};
			return blockCard;
		}

		case 'embed': {
			const parsedWidth = attrs?.width ? parseInt(attrs.width, 10) : undefined;
			const width = parsedWidth !== undefined && !isNaN(parsedWidth) ? parsedWidth : undefined;
			const embedCard: EmbedCardDefinition = {
				type: 'embedCard',
				attrs: {
					url,
					layout: DEFAULT_EMBED_LAYOUT,
					...(width !== undefined && { width }),
				},
			};
			return embedCard;
		}

		case 'inline':
		default: {
			// Inline cards must be wrapped in a paragraph in ADF
			const paragraph: ParagraphDefinition = {
				type: 'paragraph',
				content: [
					{
						type: 'inlineCard',
						attrs: {
							url,
						},
					},
				],
			};
			return paragraph;
		}
	}
};
